import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../shared/config";
import { useAuth } from "../../contexts/AuthContext";
import InscriptionPage from "./InscriptionPage";
import { AccesPaysRefuse } from "./AccesPaysRefuse";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { UserPlus, ShieldCheck, CalendarDays, Mail, Users, Lock, Server, KeyRound } from "lucide-react";
import logoFull from "../../assets/logo-full.png";
import logoOp from "../../assets/logo-op.png";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [centreId, setCentreId] = useState('');
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCentres, setLoadingCentres] = useState(true);
  const [showInscription, setShowInscription] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [refusPays, setRefusPays] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const { login, completeMfa } = useAuth();
  const navigate = useNavigate();

  // Charger la liste des centres au montage
  useEffect(() => {
    const fetchCentres = async () => {
      try {
        const response = await axios.get(`${API}/centres/public`, { timeout: 10000 });
        setCentres(response.data.centres || []);
      } catch (error) {
        console.error('Erreur chargement centres:', error);
        setCentres([]);
      } finally {
        setLoadingCentres(false);
      }
    };
    fetchCentres();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Si "all" est sélectionné ou rien, on passe null (pour les Directeurs)
      const selectedCentreId = (centreId && centreId !== 'all') ? centreId : null;
      const result = await login(email, password, selectedCentreId);
      if (result?.mfaRequired) {
        setMfaChallenge(result.challengeToken);  // → affiche l'écran code 2FA
      } else {
        navigate('/' + window.location.search);
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      const detail = error.response?.data?.detail;
      if (detail?.code === 'pays_refuse') { setRefusPays(detail); return; }
      if (typeof detail !== 'object') toast.error(detail || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const selectedCentreId = (centreId && centreId !== 'all') ? centreId : null;
      await completeMfa(mfaChallenge, mfaCode.trim(), { rememberDevice, centreId: selectedCentreId });
      navigate('/' + window.location.search);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };

  // Formulaire d'inscription
  if (showInscription) {
    return <InscriptionPage onBack={() => setShowInscription(false)} centres={centres} />;
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Panneau gauche - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A2350] p-12 flex-col justify-between relative overflow-hidden">
        {/* Halos décoratifs */}
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-[#1173D4]/25 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#1173D4]/15 rounded-full blur-2xl translate-y-1/2 -translate-x-1/3"></div>

        <div className="relative z-10 flex items-center gap-4">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg shadow-black/20 p-2">
            <img src={logoOp} alt="OphtaPilot" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white leading-tight">
              Ophta<span className="text-[#5EA8F2]">Pilot</span>
            </div>
            <div className="text-white/50 text-xs tracking-widest uppercase">Gestion de cabinet médical</div>
          </div>
        </div>

        <div className="relative z-10 space-y-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
            Pilotez votre cabinet,<br />en toute sérénité
          </h1>
          <div className="space-y-4 max-w-md">
            <div className="flex items-center gap-3 text-white/85">
              <span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center"><CalendarDays className="w-5 h-5 text-[#5EA8F2]" /></span>
              <span>Plannings, congés et équipes centralisés</span>
            </div>
            <div className="flex items-center gap-3 text-white/85">
              <span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center"><Mail className="w-5 h-5 text-[#5EA8F2]" /></span>
              <span>Courriers, documents et dossiers patients</span>
            </div>
            <div className="flex items-center gap-3 text-white/85">
              <span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center"><Users className="w-5 h-5 text-[#5EA8F2]" /></span>
              <span>Demandes de vacations et messagerie interne</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-4">
          {/* Bloc RGPD */}
          <div className="flex items-start gap-3 bg-white/[0.07] border border-white/10 rounded-2xl p-4 backdrop-blur-sm" data-testid="rgpd-panel">
            <ShieldCheck className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-semibold text-sm">Conforme RGPD</div>
              <div className="text-white/60 text-xs leading-relaxed">
                Données de santé hébergées exclusivement en Union Européenne 🇪🇺 (France).
                Chiffrement des échanges, aucun transfert hors UE, aucun traceur tiers.
              </div>
            </div>
          </div>
          <div className="text-white/35 text-xs">
            © {new Date().getFullYear()} OphtaPilot — Tous droits réservés
          </div>
        </div>
      </div>

      {/* Panneau droit - Formulaire */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F4F7FB]">
        <div className="w-full max-w-md space-y-6">
          {/* Logo */}
          <div className="flex justify-center">
            <img src={logoFull} alt="OphtaPilot — Gestion de cabinet médical" className="h-44 lg:h-48 object-contain" data-testid="login-logo" />
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-bold text-[#0A2350] mb-1">Bienvenue</h2>
            <p className="text-gray-500 text-sm">Connectez-vous à votre espace</p>
          </div>
          
          <Card className="border-0 shadow-xl shadow-[#0A2350]/5 bg-white rounded-2xl">
            <CardContent className="p-8">
              {refusPays ? (
                <AccesPaysRefuse refus={refusPays} onBack={() => setRefusPays(null)} />
              ) : mfaChallenge ? (
                <form onSubmit={handleMfaSubmit} className="space-y-5" data-testid="mfa-form">
                  <div className="flex flex-col items-center text-center gap-2">
                    <span className="w-12 h-12 rounded-2xl bg-[#1173D4]/10 flex items-center justify-center">
                      <KeyRound className="w-6 h-6 text-[#1173D4]" />
                    </span>
                    <h3 className="text-lg font-bold text-[#0A2350]">Vérification en deux étapes</h3>
                    <p className="text-sm text-gray-500">Saisissez le code à 6 chiffres de votre application d'authentification.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mfa-code" className="text-sm font-semibold text-gray-700">Code de vérification</Label>
                    <Input
                      id="mfa-code"
                      data-testid="mfa-code-input"
                      inputMode="numeric"
                      autoFocus
                      maxLength={9}
                      placeholder="123456"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      disabled={loading}
                      className="h-12 rounded-xl border-2 border-gray-200 focus:border-[#1173D4] text-center text-lg tracking-[0.3em]"
                    />
                    <p className="text-xs text-gray-400">Perdu votre téléphone ? Saisissez un code de récupération.</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <Checkbox checked={rememberDevice} onCheckedChange={(v) => setRememberDevice(Boolean(v))} data-testid="mfa-remember-checkbox" />
                    Faire confiance à cet appareil pendant 30 jours
                  </label>
                  <Button type="submit" data-testid="mfa-submit-btn" disabled={loading || mfaCode.length < 6}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1173D4] to-[#0A2350] text-white font-semibold shadow-lg shadow-[#1173D4]/25">
                    {loading ? 'Vérification...' : 'Vérifier'}
                  </Button>
                  <Button type="button" variant="ghost" data-testid="mfa-back-btn"
                    onClick={() => { setMfaChallenge(null); setMfaCode(''); }}
                    className="w-full text-gray-500">
                    ← Retour
                  </Button>
                </form>
              ) : (
              <>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                {/* Sélection du centre - Optionnel pour les Directeurs */}
                <div className="space-y-2">
                  <Label htmlFor="centre" className="text-sm font-semibold text-gray-700">
                    Centre médical
                  </Label>
                  <Select 
                    value={centreId} 
                    onValueChange={setCentreId}
                    disabled={loadingCentres}
                  >
                    <SelectTrigger 
                      id="centre"
                      data-testid="centre-select"
                      className="h-12 rounded-xl border-2 border-gray-200 focus:border-[#1173D4]"
                    >
                      <SelectValue placeholder={loadingCentres ? "Chargement..." : "Tous les centres"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <span className="font-medium">Tous les centres</span>
                      </SelectItem>
                      {centres.map((centre) => (
                        <SelectItem key={centre.id} value={centre.id}>
                          {centre.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                    Adresse email
                  </Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="votre@email.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="h-12 rounded-xl border-2 border-gray-200 focus:border-[#1173D4] focus:ring-[#1173D4]/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                    Mot de passe
                  </Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="h-12 rounded-xl border-2 border-gray-200 focus:border-[#1173D4] focus:ring-[#1173D4]/20"
                  />
                </div>
                <Button 
                  type="submit" 
                  data-testid="login-submit-btn"
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1173D4] to-[#0A2350] hover:from-[#0E5FB0] hover:to-[#081C40] text-white font-semibold shadow-lg shadow-[#1173D4]/25 transition-all duration-200" 
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Connexion...
                    </span>
                  ) : 'Se connecter'}
                </Button>
              </form>
              
              {/* Séparateur */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200"></span>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-400">Nouveau ?</span>
                </div>
              </div>
              
              {/* Bouton d'inscription */}
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowInscription(true)}
                className="w-full h-12 rounded-xl border-2 border-[#1173D4] text-[#1173D4] hover:bg-[#1173D4] hover:text-white font-semibold transition-all duration-200"
                data-testid="inscription-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Demander un accès
              </Button>
              </>
              )}
            </CardContent>
          </Card>

          {/* Mention RGPD (visible aussi sur mobile) */}
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500" data-testid="rgpd-badges">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Conforme RGPD</span>
            <span className="flex items-center gap-1.5"><Server className="w-4 h-4 text-[#1173D4]" /> Hébergé en UE 🇪🇺</span>
            <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#0A2350]" /> Chiffré</span>
          </div>

          <p className="text-center text-xs text-gray-400">
            Problème de connexion ? Contactez votre administrateur
          </p>
        </div>
      </div>
    </div>
  );
};

// Page d'inscription

export default LoginPage;
